import { useState } from 'react';
import { Plus, Key, Trash2, Clock, Activity, Shield, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiKeys, useRevokeApiKey, useAuditLog } from '../hooks/useApiKeys';
import { CreateKeyDialog } from '../components/CreateKeyDialog';
import { KpiApiDocs } from '@/features/kpis/components/KpiApiDocs';
import type { ApiKey } from '../types';
import { formatDateTime } from '@/lib/utils';

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function ApiKeysPage() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [revokingKey, setRevokingKey] = useState<ApiKey | null>(null);
  const [auditPage, setAuditPage] = useState(0);

  const { data: keys, isLoading } = useApiKeys();
  const revokeKey = useRevokeApiKey();
  const { data: auditData, isLoading: auditLoading } = useAuditLog(auditPage);

  const handleRevoke = async () => {
    if (revokingKey) {
      await revokeKey.mutateAsync(revokingKey.id);
      setRevokingKey(null);
    }
  };

  const keysList = Array.isArray(keys) ? keys : [];
  const activeKeys = keysList.filter((k) => k.is_active);
  const revokedKeys = keysList.filter((k) => !k.is_active);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Key className="h-6 w-6 text-primary" />
            API Keys
          </h1>
          <p className="text-muted-foreground">
            Manage API keys for external integrations and scripts
          </p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Key
        </Button>
      </div>

      <Tabs defaultValue="keys">
        <TabsList>
          <TabsTrigger value="keys" className="gap-1.5">
            <Key className="h-3.5 w-3.5" />
            Keys
          </TabsTrigger>
          <TabsTrigger value="audit" className="gap-1.5">
            <Activity className="h-3.5 w-3.5" />
            Audit Log
          </TabsTrigger>
          <TabsTrigger value="docs" className="gap-1.5">
            <Shield className="h-3.5 w-3.5" />
            API Docs
          </TabsTrigger>
        </TabsList>

        {/* Keys Tab */}
        <TabsContent value="keys" className="mt-4 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Active Keys</CardTitle>
              <CardDescription>
                {activeKeys.length} active key{activeKeys.length !== 1 ? 's' : ''}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : activeKeys.length === 0 ? (
                <div className="text-center py-8">
                  <Key className="h-10 w-10 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground mb-3">No API keys yet</p>
                  <Button variant="outline" size="sm" onClick={() => setIsCreateOpen(true)}>
                    <Plus className="h-3.5 w-3.5 mr-1.5" />
                    Create your first key
                  </Button>
                </div>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Key</TableHead>
                        <TableHead>Scopes</TableHead>
                        <TableHead>Rate Limit</TableHead>
                        <TableHead>Last Used</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="w-12" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activeKeys.map((key) => (
                        <TableRow key={key.id}>
                          <TableCell className="font-medium">{key.name}</TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                              {key.key_prefix}...
                            </code>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1 flex-wrap">
                              {key.scopes.map((s) => (
                                <Badge key={s} variant="outline" className="text-xs font-mono">
                                  {s}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{key.rate_limit_rpm}/min</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {timeAgo(key.last_used_at)}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDateTime(key.created_at)}
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => setRevokingKey(key)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Revoke
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {revokedKeys.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base text-muted-foreground">Revoked Keys</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Key</TableHead>
                        <TableHead>Revoked</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {revokedKeys.map((key) => (
                        <TableRow key={key.id} className="opacity-60">
                          <TableCell className="font-medium">{key.name}</TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                              {key.key_prefix}...
                            </code>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {key.revoked_at ? formatDateTime(key.revoked_at) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4" />
                API Audit Log
              </CardTitle>
              <CardDescription>All API requests made with your organisation's keys</CardDescription>
            </CardHeader>
            <CardContent>
              {auditLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : !auditData || auditData.entries.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No audit entries yet</p>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Time</TableHead>
                          <TableHead>Action</TableHead>
                          <TableHead>Resource</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>IP</TableHead>
                          <TableHead>Key</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {auditData.entries.map((entry) => (
                          <TableRow key={entry.id}>
                            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDateTime(entry.created_at)}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs font-mono">
                                {entry.action}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm font-mono">{entry.resource}</TableCell>
                            <TableCell>
                              <Badge
                                variant={entry.response_status < 400 ? 'default' : 'destructive'}
                                className="text-xs"
                              >
                                {entry.response_status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {entry.ip_address ?? '—'}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {entry.key_name ?? '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {auditData.total > 25 && (
                    <div className="flex justify-between items-center mt-4">
                      <p className="text-sm text-muted-foreground">
                        Page {auditPage + 1} of {Math.ceil(auditData.total / 25)}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={auditPage === 0}
                          onClick={() => setAuditPage((p) => p - 1)}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={(auditPage + 1) * 25 >= auditData.total}
                          onClick={() => setAuditPage((p) => p + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Docs Tab */}
        <TabsContent value="docs" className="mt-4">
          <KpiApiDocs />
        </TabsContent>
      </Tabs>

      <CreateKeyDialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)} />

      <AlertDialog open={!!revokingKey} onOpenChange={() => setRevokingKey(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API Key</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke "{revokingKey?.name}"? Any scripts using this key
              will immediately stop working. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevoke}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Revoke Key
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
